import { User } from '@auth0/auth0-spa-js';
import { SETTINGS } from '@Config';
import { STORAGE } from '@Core';
import {
  Auth0LoginResponse,
  Auth0Options,
  Auth0RegisterResponse,
  AuthModule,
  AuthProfile,
  MNGSession,
} from '@Modules/index';
import { LocalStorage } from '@Shared/storage';
import { CacheManager } from '@Shared/cache';
import { Logger, LoggerService } from '@Shared/logger';

export class Auth0Repository {
  private LoggerBase: Logger = LoggerService.createLogger('Auth0Repository');

  private _localStorageManger: LocalStorage = new LocalStorage();

  private static instance: Auth0Repository;
  static get Instance() {
    if (this.instance === null || this.instance === undefined) {
      this.instance = new Auth0Repository();
    }
    return this.instance;
  }

  private idToken: string;
  get IdToken(): string {
    return this.idToken;
  }

  private accessToken: string;
  get AccessToken(): string {
    return this.accessToken;
  }

  public Auth0User: User;

  public AuthProfile: AuthProfile;

  private checkSessionCacheManager: CacheManager<boolean>;

  constructor() {
    this.LoggerBase.trace('****  CTOR ****');
    const authOptions = AuthModule.AuthOptions;
    /**
     * This creates a cache manager for the checkSessionCache, this is used to set cache strategy on the getTokenSilently call.
     * We'll need to determine the best length of time so we getTokenSilently doesn't call the server (which takes about 200ms) but we also want SSO to work if I logged in from a site like e-edition or some other site.
     */
    this.checkSessionCacheManager = new CacheManager<boolean>(
      STORAGE.notAuthenticatedCheckSessionKey,
      authOptions.checkSessionCache.cacheOptions.storage,
      authOptions.checkSessionCache.cacheOptions.duration,
    );
  }

  public setIdToken(idToken: string) {
    this.idToken = idToken;
  }

  public setAccessToken(accessToken: string) {
    this.accessToken = accessToken;
  }

  public localStorageAuthSessionExists() {
    const localStorageSession = this._localStorageManger.getParsed<MNGSession>(
      STORAGE.sessionLocalStorageKey,
    );

    if (!localStorageSession) return false;

    return localStorageSession.idToken && localStorageSession.idToken !== '';
  }

  public hasCheckedSession(): boolean {
    const res = this.checkSessionCacheManager.getCache();
    if (!res) {
      this.checkSessionCacheManager.setCache(true);
    }
    return res;
  }

  //TODO: Need to add logic for user_metadata.customProfile.nickname
  public mapAuth0User(user: User): AuthProfile {
    this.AuthProfile = {
      auth0Id: user.sub,
      email: user.email,
      firstName: user?.given_name,
      lastName: user?.family_name,
      name: user?.name,
      displayName: user?.name || user?.nickname,
      picture: user?.picture,
      nickname: user?.nickname,
      email_verified: user?.email_verified,
      connection: user?.connection_source,
      metadata: user?.metadata,
      idToken: this.idToken,
    };
    this.LoggerBase.trace(
      'mapAuth0User',
      'this.AuthProfile => ',
      this.AuthProfile,
    );
    const connectionDescription = this.getConnectionDescription(
      user?.connection_source,
    );
    if (connectionDescription) {
      this.AuthProfile.connectionDescription = `Logged in with ${connectionDescription}`;
    }
    this.Auth0User = user;
    return this.AuthProfile;
  }

  private getConnectionDescription(connection: string): string {
    try {
      const conLC = connection.toLowerCase();

      if (conLC.includes('google')) {
        return 'Google';
      } else if (conLC.includes('facebook')) {
        return 'Facebook';
      } else if (conLC.includes('apple')) {
        return 'Apple';
      } else if (conLC.includes('anonymous-access')) {
        return 'Anonymous Access';
      } else {
        return null;
      }
    } catch (error) {
      this.LoggerBase.error('[getConnectionDescription] error => ', error);
      return null;
    }
  }

  public clearAuthData(): void {
    this.LoggerBase.trace('clearAuthData');
    this.checkSessionCacheManager.removeCache();
    this.Auth0User = null;
    this.AuthProfile = null;
    this.idToken = null;
    this.accessToken = null;
  }

  public createLocalAuthObjects(
    res: Auth0LoginResponse | Auth0RegisterResponse,
  ) {
    const fnName = '[createLocalAuthObjects] ';

    try {
      const data = res.data;
      const expirationTime = new Date().getTime() + data.expires_in * 1000;
      const options = AuthModule.AuthOptions as Auth0Options;
      const storageName = `@@auth0spajs@@::${options.clientId}::${SETTINGS.auth0.audience}::${SETTINGS.auth0.scopes}`;
      const localStorageObj = {
        body: {
          access_token: data.access_token,
          audience: SETTINGS.auth0.audience,
          client_id: options.clientId,
          expires_in: data.expires_in,
          oauthTokenScope: data.scope,
          refresh_token: data.refresh_token,
          scope: SETTINGS.auth0.scopes,
          token_type: data.token_type,
        },
        expiresAt: expirationTime,
      };

      localStorage.setItem(storageName, JSON.stringify(localStorageObj));
    } catch (e) {
      this.LoggerBase.error(fnName, 'catch.e => ', e);
    }
  }
}
